import React from "react";
import LegalPage from "./LegalPage";

export default function Privacy() {
  return (
    <LegalPage
      kicker="Legal"
      title="Privacy Policy"
      updated="February 2026"
      testId="privacy-page"
    >
      <p>
        This Privacy Policy describes how LocalizeHER ("we," "us," "our") collects, uses, and
        shares information when you use <strong>localizeher.com</strong>, the LocalizeHER member
        platform, and related services.
      </p>

      <h2>1. Information We Collect</h2>
      <ul>
        <li>
          <strong>Account information.</strong> Name, email, password (hashed), and — if you
          sign in with Google — your Google profile information (name, email, picture).
        </li>
        <li>
          <strong>Business information.</strong> Business name, category, city, description,
          logos, photos, website, social handles, and any details you choose to add to your HER
          Guide listing.
        </li>
        <li>
          <strong>Payment information.</strong> When LocalizeHER begins processing paid
          memberships, we will use a third-party payment processor that handles your card and
          billing details on our behalf. We will receive a token, the last four digits of your
          card, and the transaction status — we never store your full card number. Until paid
          memberships launch, we do not collect any payment information.
        </li>
        <li>
          <strong>Usage information.</strong> Pages viewed, clicks, device and browser type,
          approximate location (derived from IP), and timestamps. We use this to improve the
          platform and prevent abuse.
        </li>
        <li>
          <strong>Cookies.</strong> See Section 6 below.
        </li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <ul>
        <li>To operate your account, the HER Guide directory, events, and community features;</li>
        <li>To process payments and send transactional emails (receipts, renewal notices);</li>
        <li>To send marketing emails about LocalizeHER (you can unsubscribe at any time);</li>
        <li>To improve the platform, our content, and our member experience;</li>
        <li>To prevent fraud, enforce our Terms, and comply with the law.</li>
      </ul>

      <h2>3. How We Share Your Information</h2>
      <p>
        We do <strong>not</strong> sell your personal information. We share it only with:
      </p>
      <ul>
        <li>
          <strong>Service providers</strong> who help us run the platform — Google (OAuth sign-in),
          MongoDB Atlas (hosting), email providers, and analytics tools. These vendors are bound
          by data-protection agreements.
        </li>
        <li>
          <strong>The public, when you choose to be public.</strong> Your HER Guide listing is
          designed to be publicly discoverable. Anything you add to a public profile may be
          indexed by search engines.
        </li>
        <li>
          <strong>Legal requirements.</strong> If we're required by law, subpoena, or court order
          to disclose your information, we will.
        </li>
        <li>
          <strong>Business transfers.</strong> If LocalizeHER is acquired or merged, your data
          may transfer to the new owner subject to this Policy.
        </li>
      </ul>

      <h2>4. Your Rights</h2>
      <p>
        Depending on where you live (California, Virginia, Colorado, EU, UK, etc.), you may have
        the right to:
      </p>
      <ul>
        <li>Access the personal information we hold about you;</li>
        <li>Correct or delete that information;</li>
        <li>Export your data in a portable format;</li>
        <li>Opt out of marketing emails (every email has an unsubscribe link);</li>
        <li>Opt out of the "sale" or "sharing" of personal data (we don't sell or share for
          cross-context behavioural advertising, but the option is here for the record).</li>
      </ul>
      <p>
        Email <a href="mailto:hello@localizeher.com">hello@localizeher.com</a> to exercise any of
        these rights. We'll respond within the timeframe required by your applicable law.
      </p>

      <h2>5. Data Retention &amp; Security</h2>
      <p>
        We keep your information for as long as your account is active and for a reasonable period
        afterward to satisfy legal, accounting, or fraud-prevention obligations. We protect your
        data with industry-standard measures: passwords are hashed with bcrypt, sessions are
        delivered over HTTPS in httpOnly cookies, sensitive endpoints are rate-limited, and our
        databases sit behind a private network. No system is 100% secure — but we work hard to
        treat your data the way we'd want ours treated.
      </p>

      <h2>6. Cookies &amp; Tracking</h2>
      <p>We use a small set of cookies:</p>
      <ul>
        <li>
          <strong>Strictly necessary</strong> — your authentication session
          (<code>session_token</code>) and cookie-consent preference. These cannot be disabled.
        </li>
        <li>
          <strong>Analytics</strong> — anonymous page-view tracking. You can opt out via our
          cookie banner.
        </li>
      </ul>
      <p>
        You can also manage cookies via your browser settings. Disabling strictly-necessary cookies
        will break sign-in.
      </p>

      <h2>7. Children's Privacy</h2>
      <p>
        LocalizeHER is intended for adults running businesses. We don't knowingly collect personal
        information from anyone under 18. If you believe we have, contact us and we'll delete it.
      </p>

      <h2>8. International Transfers</h2>
      <p>
        Our service providers may store data in countries other than yours. By using LocalizeHER
        you consent to those transfers, which are protected by standard contractual safeguards.
      </p>

      <h2>9. Changes to This Policy</h2>
      <p>
        We'll post any material change here and notify active members by email. The "Last updated"
        date at the top reflects the most recent revision.
      </p>

      <h2>10. Contact</h2>
      <p>
        Privacy questions? <a href="mailto:hello@localizeher.com">hello@localizeher.com</a> —
        we'll route you to the right person.
      </p>

      <p style={{ marginTop: "2rem", fontSize: "0.85rem", color: "var(--lh-greige)" }}>
        <em>
          This Privacy Policy is a starting template and should be reviewed by privacy counsel
          before being held out as final.
        </em>
      </p>
    </LegalPage>
  );
}
